import { useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { trackPiecanHandoffClick } from "@/services/analytics";

const PIECAN_FALLBACK_URL = "https://piecan.ai/image-to-video";

/**
 * Open backdoor at `/go/piecan` — the single link we drop into emails:
 * `https://pixelmaker.ai/go/piecan`. On mount it mints a FRESH single-use
 * PieCan referral token via the public `GET /partner/piecan-backdoor` endpoint
 * (unique id per call → PieCan's first-claim-wins dedup never blocks a later
 * visitor) and hard-redirects to it. Works WITHOUT sign-in: no auth gate, no
 * sign-in prompt. Falls back to the bare PieCan origin on any error.
 */
export function PiecanRedirectPage() {
  // StrictMode double-invokes effects in dev; guard so we only mint/redirect
  // once per real mount.
  const hasRedirectedRef = useRef(false);

  useEffect(() => {
    if (hasRedirectedRef.current) return;
    hasRedirectedRef.current = true;

    let cancelled = false;

    (async () => {
      let url = PIECAN_FALLBACK_URL;
      try {
        const res = await api.get<{ success: boolean; url: string }>(
          "/partner/piecan-backdoor",
        );
        if (res?.url) url = res.url;
      } catch {
        // Fall through to the bare PieCan origin.
      }

      if (cancelled) return;

      // Fire-and-forget; the redirect follows regardless.
      trackPiecanHandoffClick(undefined, "email_redirect");

      window.location.replace(url);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 text-center px-6">
      <Loader2 className="size-8 text-violet-400 animate-spin" />
      <p className="text-sm text-[#888]">Taking you to PieCan…</p>
    </div>
  );
}
